// src/utils/currentIdentity.js
// One answer to "who is signed in?" for the whole site.
//   A signed-in partner (travel agent) wins: one identity at a time (agentAuth signs the
//   Firebase customer out when a partner signs in, and AuthContext clears the partner when a
//   customer signs in).
//   Otherwise the Firebase customer, otherwise nobody.
//
// Use this instead of reading auth.currentUser directly, so partners are treated as logged in
// on /chat, /search-form, booking pages, booking history and everywhere else.
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { getAgentSession, AGENT_SESSION_EVENT } from "./agentAuth";

/**
 * { kind: "partner" | "customer", email, phone, name, token, uid } or null.
 * email is lower-case. token: partner session token, or "" for customers (use getIdToken()).
 */
export function getCurrentIdentity() {
    const s = getAgentSession();
    if (s?.profile?.email) {
        const p = s.profile;
        return {
            kind: "partner",
            email: String(p.email).trim().toLowerCase(),
            phone: p.phone || "",
            name: p.business_name || p.name || "",
            token: s.token,
            uid: p.user_id,
        };
    }
    const u = auth?.currentUser;
    if (u) {
        return {
            kind: "customer",
            email: (u.email || "").trim().toLowerCase(),
            phone: u.phoneNumber || "",
            name: u.displayName || "",
            token: "",
            uid: u.uid,
        };
    }
    return null;
}

/** Email, else phone, of whoever is signed in ("" when nobody is). The id bookings are stored under. */
export function getCurrentContact() {
    const id = getCurrentIdentity();
    return id ? (id.email || id.phone) : "";
}

/** Auth token for our APIs: partner session token, or a fresh Firebase ID token. */
export async function getCurrentAuthToken() {
    const id = getCurrentIdentity();
    if (!id) return null;
    if (id.kind === "partner") return id.token;
    try { return await auth.currentUser.getIdToken(); } catch { return null; }
}

/** Calls cb(identity) now-ish and whenever either login changes. Returns an unsubscribe function. */
export function onIdentityChange(cb) {
    const fire = () => cb(getCurrentIdentity());
    const unsubFirebase = onAuthStateChanged(auth, fire);
    window.addEventListener(AGENT_SESSION_EVENT, fire);
    window.addEventListener("storage", fire);
    return () => {
        unsubFirebase();
        window.removeEventListener(AGENT_SESSION_EVENT, fire);
        window.removeEventListener("storage", fire);
    };
}
